import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  Link,
  MenuItem,
  Select,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import DnsOutlinedIcon from '@mui/icons-material/DnsOutlined';
import CloudOutlinedIcon from '@mui/icons-material/CloudOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import CustomTextInput from '../../../components/CustomTextInput';
import { apiSettingsCardSx } from '../../../constants/apiSettingsCardTypography';
import {
  DEFAULT_EMAIL_SETTINGS_FORM,
  EMAIL_PROVIDER_MICROSOFT_GRAPH,
  EMAIL_PROVIDER_SMTP,
  ENTRA_APP_REGISTRATION_GUIDE_URL,
  GRAPH_STATUS_EXPIRED,
  buildEmailSettingsPayload,
  buildEmailSettingsSchema,
  clientSecretRequired,
  hasStoredClientSecret,
  hasStoredPassword,
  mapEmailSettingsToForm,
} from '../constants/emailSettings';
import {
  useGetEmailSettingsHandler,
  useTestEmailSettingsHandler,
  useUpdateEmailSettingsHandler,
} from '../dataHandlers/emailSettingsHandlers';

const fieldGridSx = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
  gap: 2,
};

const sectionLabelSx = {
  fontSize: '0.8125rem',
  fontWeight: 500,
  color: 'text.secondary',
};

const PROVIDER_OPTIONS = [
  {
    value: EMAIL_PROVIDER_SMTP,
    title: 'SMTP server',
    subtitle: 'Use your existing mail server',
    icon: DnsOutlinedIcon,
  },
  {
    value: EMAIL_PROVIDER_MICROSOFT_GRAPH,
    title: 'Microsoft 365',
    subtitle: 'Send through Microsoft Graph',
    icon: CloudOutlinedIcon,
  },
];

export default function EmailSettingsCard() {
  const { emailSettings, isLoading } = useGetEmailSettingsHandler();
  const [showPassword, setShowPassword] = useState(false);
  const [showClientSecret, setShowClientSecret] = useState(false);
  const [testError, setTestError] = useState('');
  const [testSuccess, setTestSuccess] = useState('');
  const initialPasswordRef = useRef('');
  const initialGraphRef = useRef({ tenant_id: '', client_id: '', client_secret: '' });
  const validationFlags = useRef({ passwordRequired: true, clientSecretRequired: true });
  const passwordExists = hasStoredPassword(emailSettings);
  const secretExists = hasStoredClientSecret(emailSettings);

  const resolver = useMemo(
    () => (values, context, options) =>
      yupResolver(buildEmailSettingsSchema(validationFlags.current))(values, context, options),
    [],
  );

  const {
    control,
    handleSubmit,
    reset,
    watch,
    trigger,
    getValues,
    formState: { errors, isDirty },
  } = useForm({
    resolver,
    defaultValues: DEFAULT_EMAIL_SETTINGS_FORM,
  });

  const isEnabled = watch('enabled');
  const provider = watch('provider');
  const tenantId = watch('tenant_id');
  const clientId = watch('client_id');
  validationFlags.current = {
    passwordRequired: !passwordExists,
    clientSecretRequired: clientSecretRequired(emailSettings, { tenant_id: tenantId, client_id: clientId }),
  };

  useEffect(() => {
    if (isLoading) {
      return;
    }

    const formValues = mapEmailSettingsToForm(emailSettings);
    initialPasswordRef.current = formValues.password;
    initialGraphRef.current = {
      tenant_id: formValues.tenant_id,
      client_id: formValues.client_id,
      client_secret: formValues.client_secret,
    };
    reset(formValues);
  }, [emailSettings, isLoading, reset]);

  const { onSubmit: submitUpdate, isPending } = useUpdateEmailSettingsHandler({
    onSuccess: (data) => {
      const formValues = mapEmailSettingsToForm(data);
      initialPasswordRef.current = formValues.password;
      initialGraphRef.current = {
        tenant_id: formValues.tenant_id,
        client_id: formValues.client_id,
        client_secret: formValues.client_secret,
      };
      reset(formValues);
      setTestError('');
      setTestSuccess('');
    },
  });

  const { onTest, isPending: isTesting } = useTestEmailSettingsHandler({
    onSuccess: () => {
      setTestError('');
      setTestSuccess('Test email sent to your account email address.');
    },
    onError: (message) => {
      setTestSuccess('');
      setTestError(message);
    },
  });

  const graphExpired = emailSettings?.graph?.status === GRAPH_STATUS_EXPIRED;
  const showMicrosoft = isEnabled && provider === EMAIL_PROVIDER_MICROSOFT_GRAPH;
  const showSmtp = isEnabled && provider !== EMAIL_PROVIDER_MICROSOFT_GRAPH;
  const busy = isPending || isTesting;

  const onSubmit = (values) => {
    submitUpdate(buildEmailSettingsPayload(values, initialPasswordRef.current, initialGraphRef.current));
  };

  const onTestConnection = async () => {
    const valid = await trigger();
    if (!valid) {
      return;
    }

    setTestError('');
    setTestSuccess('');
    onTest(buildEmailSettingsPayload(getValues(), initialPasswordRef.current, initialGraphRef.current));
  };

  if (isLoading) {
    return (
      <Box sx={{ ...apiSettingsCardSx.container, display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Box sx={apiSettingsCardSx.container} component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
      <Stack spacing={2.5}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
            <MailOutlineIcon fontSize="small" color="primary" />
            <Typography sx={apiSettingsCardSx.title}>Email sender</Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={apiSettingsCardSx.description}>
            Send business notifications from your own email domain.
          </Typography>
        </Box>

        <Divider />

        <Controller
          name="enabled"
          control={control}
          render={({ field }) => (
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Use my email provider
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={apiSettingsCardSx.description}>
                  When off, notifications keep using the Marmin sender.
                </Typography>
              </Box>
              <Switch
                checked={field.value}
                onChange={(event) => field.onChange(event.target.checked)}
                disabled={busy}
                inputProps={{ 'aria-label': 'Use my email provider' }}
              />
            </Stack>
          )}
        />

        <Divider />

        <Box
          sx={{
            display: 'flex',
            gap: 1.5,
            alignItems: 'flex-start',
            px: 2,
            py: 1.5,
            borderRadius: 1,
            bgcolor: 'action.hover',
            borderLeft: '3px solid',
            borderColor: 'info.main',
          }}
        >
          <InfoOutlinedIcon fontSize="small" color="info" sx={{ mt: 0.15 }} />
          <Typography variant="body2" color="text.secondary">
            Live email setup requires a verified administrator account for this organization. Your current preview
            sign-in is a demo account.
          </Typography>
        </Box>

        {graphExpired && (
          <Alert severity="warning">
            Microsoft 365 app registration credentials were rejected. Update them and save to resume sending emails.
          </Alert>
        )}

        {isEnabled && (
          <Stack spacing={3}>
            <Stack spacing={1}>
              <Typography sx={sectionLabelSx}>Choose your email provider</Typography>
              <Controller
                name="provider"
                control={control}
                render={({ field }) => (
                  <FormControl sx={{ maxWidth: 420 }} disabled={busy}>
                    <Select
                      value={field.value}
                      onChange={(event) => field.onChange(event.target.value)}
                      renderValue={(value) => <ProviderValue value={value} />}
                      inputProps={{ 'aria-label': 'Choose your email provider' }}
                    >
                      {PROVIDER_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          <ProviderValue value={option.value} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Stack>

            <Stack spacing={1.5}>
              <Typography sx={sectionLabelSx}>What recipients will see</Typography>
              <Box sx={fieldGridSx}>
                <CustomTextInput
                  name="from_name"
                  label="Sender name"
                  control={control}
                  errors={errors}
                  size="small"
                  required
                  disabled={busy}
                />
                <CustomTextInput
                  name="from_email"
                  label="Sender email address"
                  control={control}
                  errors={errors}
                  size="small"
                  required
                  disabled={busy}
                />
              </Box>
            </Stack>

            {showSmtp && (
              <Stack spacing={1.5}>
                <Typography sx={sectionLabelSx}>SMTP connection</Typography>
                <Box sx={fieldGridSx}>
                  <CustomTextInput
                    name="host"
                    label="SMTP server"
                    control={control}
                    errors={errors}
                    size="small"
                    required
                    disabled={busy}
                  />
                  <CustomTextInput
                    name="port"
                    label="SMTP port"
                    type="number"
                    control={control}
                    errors={errors}
                    size="small"
                    required
                    disabled={busy}
                  />
                </Box>
                <Box sx={fieldGridSx}>
                  <CustomTextInput
                    name="username"
                    label="SMTP username"
                    control={control}
                    errors={errors}
                    size="small"
                    required
                    disabled={busy}
                    autoComplete="off"
                  />
                  <SecretField
                    name="password"
                    label="SMTP password"
                    control={control}
                    errors={errors}
                    required
                    visible={showPassword}
                    onToggle={() => setShowPassword((prev) => !prev)}
                    disabled={busy}
                    helperText={passwordExists ? 'Leave unchanged to keep the current password.' : undefined}
                  />
                </Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary">
                    Encrypted connection required: TLS on 465, STARTTLS on 587.
                  </Typography>
                </Stack>
              </Stack>
            )}

            {showMicrosoft && (
              <Stack spacing={1.5}>
                <Typography sx={sectionLabelSx}>Microsoft 365 connection</Typography>
                <Box sx={fieldGridSx}>
                  <CustomTextInput
                    name="tenant_id"
                    label="Directory (tenant) ID"
                    control={control}
                    errors={errors}
                    size="small"
                    required
                    disabled={busy}
                  />
                  <CustomTextInput
                    name="client_id"
                    label="Application (client) ID"
                    control={control}
                    errors={errors}
                    size="small"
                    required
                    disabled={busy}
                  />
                </Box>
                <Box sx={{ maxWidth: { sm: 'calc(50% - 8px)' } }}>
                  <SecretField
                    name="client_secret"
                    label="Client secret"
                    control={control}
                    errors={errors}
                    required
                    visible={showClientSecret}
                    onToggle={() => setShowClientSecret((prev) => !prev)}
                    disabled={busy}
                    helperText={secretExists ? 'Leave unchanged to keep the current client secret.' : undefined}
                  />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Use an Entra application with Mail.Send application permission and administrator consent. Restrict it
                  to your sending mailbox.
                </Typography>
                <Link
                  href={ENTRA_APP_REGISTRATION_GUIDE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, width: 'fit-content' }}
                >
                  Microsoft setup guide
                  <OpenInNewIcon sx={{ fontSize: 14 }} />
                </Link>
              </Stack>
            )}

            <Stack spacing={0.75}>
              <Typography sx={sectionLabelSx}>Confirm your sender</Typography>
              <Typography variant="body2" color="text.secondary">
                Save your settings, then send a test email to your verified sign-in address. Provider acceptance does
                not guarantee inbox delivery.
              </Typography>
            </Stack>
          </Stack>
        )}

        {testError && <Alert severity="error">{testError}</Alert>}
        {testSuccess && <Alert severity="success">{testSuccess}</Alert>}

        <Stack direction="row" justifyContent="flex-end" spacing={1.5}>
          {isEnabled && (
            <Button
              type="button"
              variant="outlined"
              startIcon={<SendOutlinedIcon />}
              onClick={onTestConnection}
              disabled={busy}
              sx={{ textTransform: 'none' }}
            >
              {isTesting ? 'Sending...' : 'Send test email'}
            </Button>
          )}
          <Button
            type="submit"
            variant="contained"
            startIcon={<SaveOutlinedIcon />}
            sx={{ ...apiSettingsCardSx.button, minWidth: 180 }}
            disabled={busy || !isDirty}
          >
            {isPending ? 'Saving...' : 'Save email settings'}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}

function ProviderValue({ value }) {
  const option = PROVIDER_OPTIONS.find((item) => item.value === value) ?? PROVIDER_OPTIONS[0];
  const Icon = option.icon;

  return (
    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 0.5 }}>
      <Icon fontSize="small" />
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
          {option.title}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.3 }}>
          {option.subtitle}
        </Typography>
      </Box>
    </Stack>
  );
}

function SecretField({ name, label, control, errors, visible, onToggle, disabled, helperText, required }) {
  return (
    <CustomTextInput
      name={name}
      label={label}
      type={visible ? 'text' : 'password'}
      control={control}
      errors={errors}
      size="small"
      required={required}
      disabled={disabled}
      autoComplete="new-password"
      helperText={helperText}
      endAdornment={
        <InputAdornment position="end">
          <IconButton
            onClick={onToggle}
            edge="end"
            size="small"
            aria-label={visible ? `Hide ${label}` : `Show ${label}`}
          >
            {visible ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
          </IconButton>
        </InputAdornment>
      }
    />
  );
}
